import { z } from 'zod';
import { BARCODE_SYMBOLOGIES, PRICE_LIST_KINDS, PRICE_MODES, UNITS_OF_MEASURE, } from '../constants.js';
import { bpsSchema, idSchema, paginationSchema, positivePaisaSchema, } from './common.js';
/* -------------------------------------------------------------------------- */
/* Categories                                                                  */
/* -------------------------------------------------------------------------- */
export const createCategorySchema = z.object({
    name: z.string().trim().min(1).max(120),
    parentId: idSchema.nullable().optional(),
    code: z.string().trim().max(32).optional(),
    sortOrder: z.number().int().min(0).max(9_999).default(0),
    onlineVisible: z.boolean().default(true),
});
export const updateCategorySchema = createCategorySchema.partial();
/* -------------------------------------------------------------------------- */
/* Products and variants                                                       */
/* -------------------------------------------------------------------------- */
/** A price as entered by a human: rupees with up to 2 decimals, or paisa. */
export const priceInputSchema = z.union([
    z.number().int().min(0),
    z
        .string()
        .trim()
        .regex(/^\d+(\.\d{1,2})?$/, 'Enter an amount like 125 or 125.50')
        .transform((v) => Math.round(Number(v) * 100)),
]);
/** A quantity as entered by a human: up to 3 decimals, stored as thousandths. */
export const qtyInputSchema = z.union([
    z.number().int().min(0),
    z
        .string()
        .trim()
        .regex(/^\d+(\.\d{1,3})?$/, 'Enter a quantity like 1 or 0.250')
        .transform((v) => Math.round(Number(v) * 1000)),
]);
export const variantSchema = z.object({
    id: idSchema.optional(),
    name: z.string().trim().min(1).max(120).default('Default'),
    sku: z.string().trim().max(64).nullable().optional(),
    unit: z.enum(UNITS_OF_MEASURE).default('PCS'),
    packSizeMilli: z.number().int().min(1).default(1_000),
    attributes: z.record(z.string(), z.string()).nullable().optional(),
    priceMode: z.enum(PRICE_MODES).optional(),
    pricePaisa: positivePaisaSchema.default(0),
    mrpPaisa: positivePaisaSchema.nullable().optional(),
    isActive: z.boolean().default(true),
    /** Codes to attach on save. Supplier codes are scanned; ours are generated. */
    barcodes: z
        .array(z.object({
        code: z.string().trim().min(4).max(48),
        symbology: z.enum(BARCODE_SYMBOLOGIES).default('EAN13'),
        packSizeMilli: z.number().int().min(1).default(1_000),
        isPrimary: z.boolean().default(false),
    }))
        .max(20)
        .default([]),
    /**
     * Optional stock-on-hand as of creation, posted as an `OPENING` movement
     * for one shop once the variant exists (`createProduct` — new product
     * creation was otherwise the only write path in the app that couldn't put
     * any stock behind what it just created; every other quantity change goes
     * through `applyMovements`, same here). Not offered for a batch-tracked
     * variant — that needs a batch number/expiry a goods receipt collects,
     * not this form.
     */
    openingStock: z
        .object({
        shopId: idSchema,
        qtyMilli: qtyInputSchema,
        unitCostPaisa: priceInputSchema.default(0),
    })
        .optional(),
});
export const createProductSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    nameUr: z.string().trim().max(200).nullable().optional(),
    code: z.string().trim().max(64).nullable().optional(),
    description: z.string().trim().max(2_000).nullable().optional(),
    brand: z.string().trim().max(120).nullable().optional(),
    categoryId: idSchema.nullable().optional(),
    isService: z.boolean().default(false),
    trackStock: z.boolean().default(true),
    batchTracking: z.boolean().default(false),
    serialTracking: z.boolean().default(false),
    onlineVisible: z.boolean().default(false),
    taxRateBps: bpsSchema.default(0),
    taxExempt: z.boolean().default(false),
    // FBR fields — validated against the cached reference data before a
    // product can reach a live fiscal invoice (docs/FBR.md §8).
    hsCode: z.string().trim().max(16).nullable().optional(),
    uoMFbr: z.string().trim().max(64).nullable().optional(),
    saleType: z.string().trim().max(120).nullable().optional(),
    isActive: z.boolean().default(true),
    /** At least one — a "simple" product just has one implicit variant. */
    variants: z.array(variantSchema).min(1).max(200),
    /** Generate an internal barcode for any variant that has none. */
    generateBarcodes: z.boolean().default(true),
})
    .refine((v) => !(v.isService && v.trackStock), {
    message: 'A service cannot track stock',
    path: ['trackStock'],
})
    .refine((v) => !(v.serialTracking && v.batchTracking), {
    message: 'Track either batches or serials, not both',
    path: ['serialTracking'],
})
    .refine((v) => v.variants.filter((x) => x.isActive).length > 0, { message: 'Keep at least one active variant', path: ['variants'] });
export const updateProductSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    nameUr: z.string().trim().max(200).nullable().optional(),
    code: z.string().trim().max(64).nullable().optional(),
    description: z.string().trim().max(2_000).nullable().optional(),
    brand: z.string().trim().max(120).nullable().optional(),
    categoryId: idSchema.nullable().optional(),
    trackStock: z.boolean().optional(),
    batchTracking: z.boolean().optional(),
    serialTracking: z.boolean().optional(),
    onlineVisible: z.boolean().optional(),
    taxRateBps: bpsSchema.optional(),
    taxExempt: z.boolean().optional(),
    hsCode: z.string().trim().max(16).nullable().optional(),
    uoMFbr: z.string().trim().max(64).nullable().optional(),
    saleType: z.string().trim().max(120).nullable().optional(),
    isActive: z.boolean().optional(),
    variants: z.array(variantSchema).min(1).max(200).optional(),
});
export const bulkOnlineVisibilitySchema = z.object({
    productIds: z.array(idSchema).min(1).max(500),
    onlineVisible: z.boolean(),
});
export const listProductsSchema = paginationSchema.extend({
    q: z.string().trim().max(200).optional(),
    categoryId: idSchema.optional(),
    isActive: z.coerce.boolean().optional(),
    onlineVisible: z.coerce.boolean().optional(),
    trackStock: z.coerce.boolean().optional(),
    /** Include per-shop stock for the active shop. */
    withStock: z.coerce.boolean().default(false),
    sortBy: z.enum(['name', 'code', 'createdAt', 'updatedAt']).default('name'),
    sortDir: z.enum(['asc', 'desc']).default('asc'),
});
/* -------------------------------------------------------------------------- */
/* Barcodes                                                                    */
/* -------------------------------------------------------------------------- */
export const attachBarcodeSchema = z.object({
    variantId: idSchema,
    code: z.string().trim().min(4).max(48),
    symbology: z.enum(BARCODE_SYMBOLOGIES).default('EAN13'),
    packSizeMilli: z.number().int().min(1).default(1_000),
    isPrimary: z.boolean().default(false),
});
export const generateBarcodeSchema = z.object({
    variantId: idSchema,
    /** Overrides the business's `barcode.symbology` setting for this one code. */
    symbology: z.enum(['EAN13', 'CODE128']).optional(),
    makePrimary: z.boolean().default(true),
});
/** The bill screen, goods receipt, stocktake and refund all post this. */
export const scanSchema = z.object({
    input: z.string().trim().min(1).max(64),
    /** Present when the caller wants stock/price resolved in the same round trip. */
    shopId: idSchema.optional(),
    qtyMilli: z.number().int().min(1).optional(),
    customerId: idSchema.optional(),
});
/**
 * The bill screen's offline catalog cache (README.md Phase 21) — every
 * simple (non-batch, non-serial-tracked) sellable variant for one shop,
 * with its resolved counter price baked in, so a sale can start from
 * scratch while the browser has no connection at all.
 */
export const offlineCatalogSnapshotQuerySchema = z.object({
    shopId: idSchema,
});
/* -------------------------------------------------------------------------- */
/* Price lists                                                                 */
/* -------------------------------------------------------------------------- */
export const createPriceListSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    kind: z.enum(PRICE_LIST_KINDS).default('CUSTOM'),
    priority: z.number().int().min(0).max(1_000).default(0),
    validFrom: z.coerce.date().nullable().optional(),
    validTo: z.coerce.date().nullable().optional(),
    shopIds: z.array(idSchema).default([]),
    isActive: z.boolean().default(true),
})
    .refine((v) => !v.validFrom || !v.validTo || v.validFrom <= v.validTo, {
    message: '"Valid from" must be on or before "valid to"',
    path: ['validTo'],
})
    .refine((v) => v.kind !== 'PROMO' || v.shopIds.length > 0, {
    message: 'A promo must be assigned to at least one shop',
    path: ['shopIds'],
});
export const updatePriceListSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    priority: z.number().int().min(0).max(1_000).optional(),
    validFrom: z.coerce.date().nullable().optional(),
    validTo: z.coerce.date().nullable().optional(),
    shopIds: z.array(idSchema).optional(),
    isActive: z.boolean().optional(),
});
export const upsertPriceItemsSchema = z.object({
    items: z
        .array(z.object({
        variantId: idSchema,
        priceMode: z.enum(PRICE_MODES).default('INCL'),
        pricePaisa: positivePaisaSchema,
        minQtyMilli: z.number().int().min(0).default(0),
        taxRateBps: bpsSchema.nullable().optional(),
    }))
        .min(1)
        .max(5_000),
});
export const removePriceItemsSchema = z.object({
    variantIds: z.array(idSchema).min(1).max(5_000),
});
/** "What would this cost right now, and why?" — the price-explain panel. */
export const resolvePriceQuerySchema = z.object({
    variantId: idSchema,
    shopId: idSchema.optional(),
    qtyMilli: z.coerce.number().int().min(1).default(1_000),
    customerId: idSchema.optional(),
    at: z.coerce.date().optional(),
    explain: z.coerce.boolean().default(false),
});
/* -------------------------------------------------------------------------- */
/* Import / export                                                             */
/* -------------------------------------------------------------------------- */
/**
 * One row of the product import sheet. Kept deliberately flat and forgiving:
 * a shopkeeper's spreadsheet is not a database export, so blanks fall back to
 * sensible defaults and only genuinely unusable rows are rejected.
 */
export const importRowSchema = z.object({
    name: z.string().trim().min(1).max(200),
    code: z.string().trim().max(64).optional(),
    category: z.string().trim().max(200).optional(),
    brand: z.string().trim().max(120).optional(),
    variant: z.string().trim().max(120).optional(),
    sku: z.string().trim().max(64).optional(),
    barcode: z.string().trim().max(48).optional(),
    unit: z.enum(UNITS_OF_MEASURE).optional(),
    packSize: z.coerce.number().min(0).optional(),
    price: z.coerce.number().min(0).optional(),
    mrp: z.coerce.number().min(0).optional(),
    cost: z.coerce.number().min(0).optional(),
    taxRatePct: z.coerce.number().min(0).max(100).optional(),
    taxExempt: z.coerce.boolean().optional(),
    priceIncludesTax: z.coerce.boolean().optional(),
    openingStock: z.coerce.number().min(0).optional(),
    reorderLevel: z.coerce.number().min(0).optional(),
    hsCode: z.string().trim().max(16).optional(),
});
export const importProductsSchema = z.object({
    /** Base64 XLSX/CSV payload. */
    fileBase64: z.string().min(1),
    fileName: z.string().trim().max(260).optional(),
    /**
     * Validate and report only — nothing is written. The UI always runs this
     * first, so a shopkeeper sees exactly what a 5,000-row sheet will do before
     * it happens.
     */
    dryRun: z.boolean().default(true),
    /** Update products matched by code/SKU/barcode instead of failing on them. */
    updateExisting: z.boolean().default(true),
    /** Write opening stock into this shop (needs `inventory.opening`). */
    openingStockShopId: idSchema.optional(),
    /** Price list the `price` column writes to. Defaults to the business default. */
    priceListId: idSchema.optional(),
});
export const exportProductsSchema = z.object({
    categoryId: idSchema.optional(),
    isActive: z.coerce.boolean().optional(),
    includeStock: z.coerce.boolean().default(false),
    shopId: idSchema.optional(),
});
//# sourceMappingURL=catalog.js.map